import { flushSync } from 'react-dom';

type ViewTransitionDoc = Document & {
  startViewTransition?: (update: () => Promise<void> | void) => { finished: Promise<void> };
};

/** The live preview's frame (PreviewPane) and the editor's title (DetailShell) carry these. */
export const STAGE_NAME = 'page-stage';
export const TITLE_NAME = 'page-title';

/** How long the morph waits for the editor to draw its preview before it lets go. */
const ARRIVAL_LIMIT_MS = 600;

/**
 * Open a page's editor from its tile with the tile growing into the page's live preview and
 * its name settling into the editor's title (View Transitions). Returns false, doing
 * nothing, where the browser has no View Transitions or the visitor asked for reduced
 * motion: the caller's link then navigates as any link does.
 */
export function morphToEditor(
  tile: HTMLElement,
  title: HTMLElement | null,
  go: () => void,
): boolean {
  const doc = document as ViewTransitionDoc;
  if (typeof doc.startViewTransition !== 'function') return false;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  tile.style.setProperty('view-transition-name', STAGE_NAME);
  title?.style.setProperty('view-transition-name', TITLE_NAME);
  doc.startViewTransition(async () => {
    tile.style.removeProperty('view-transition-name');
    title?.style.removeProperty('view-transition-name');
    flushSync(go);
    // The editor opens at its top, not at the home's scroll position.
    window.scrollTo(0, 0);
    await arrived();
  });
  return true;
}

/**
 * Resolves once the editor has drawn its preview stage, or after a short limit. The screen
 * holds the old picture while it waits, so the limit stays well under a perceptible stall.
 * Timers, not animation frames: rendering is paused during the update, frames may not run.
 */
function arrived(): Promise<void> {
  return new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      if (document.querySelector('[data-page-stage]') || Date.now() - start > ARRIVAL_LIMIT_MS)
        resolve();
      else setTimeout(poll, 16);
    };
    poll();
  });
}
